import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

interface CallbackSummaryCardProps {
  data: {
    needed: number
    queued: number
    completed: number
    resolved: number
    dismissed: number
  }
  loading?: boolean
}

const statuses = [
  { key: 'needed', label: 'Needed', color: 'bg-amber-500' },
  { key: 'queued', label: 'Queued', color: 'bg-blue-500' },
  { key: 'completed', label: 'Completed', color: 'bg-green-500' },
  { key: 'resolved', label: 'Resolved', color: 'bg-green-700' },
  { key: 'dismissed', label: 'Dismissed', color: 'bg-gray-400' },
] as const

export default function CallbackSummaryCard({ data, loading = false }: CallbackSummaryCardProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Callback Summary</CardTitle>
          <CardDescription>Callback lifecycle status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Loading...</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  const total = Object.values(data).reduce((sum, v) => sum + v, 0)

  if (total === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Callback Summary</CardTitle>
          <CardDescription>Callback lifecycle status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] flex items-center justify-center text-muted-foreground">
            No callbacks in this period
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Callback Summary</CardTitle>
        <CardDescription>Callback lifecycle status</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {statuses.map(({ key, label, color }) => {
            const value = data[key]
            const pct = total > 0 ? (value / total) * 100 : 0
            return (
              <div key={key}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium">{label}</span>
                  <span className="text-muted-foreground">{value} ({pct.toFixed(1)}%)</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${color}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
